import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useCallback, useMemo, useState } from 'react'
import { Button, InteractionTag, Text, Tooltip } from '@fluentui/react-components'
import { AddFilled, AddRegular, bundleIcon, FolderArrowRightFilled, FolderArrowRightRegular } from '@fluentui/react-icons'
import { DateTime } from 'luxon'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import BaseTimeOverview, { BaseTimeOverviewProps, ExtendedTimeOverviewDto, TimeFilterType } from '../../v2/pages/Time/BaseTimeOverview'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { Billable } from '../../config/registration/timeConfig'
import { getTemplateInterval } from '../../components/Form/Finance/Invoice/InvoiceFile'
import { InvoiceApprovalMutationDto, UserInvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'
import { TimeMutationDto } from '../../config/registrationConfig'
import { BaseMutableOverviewActionButtonProps } from '../BaseMutableOverview/BaseMutableOverview'
import MoveTimesDialog from './MoveTimesDialog'

const AddIcon = bundleIcon(AddFilled, AddRegular)
const MoveIcon = bundleIcon(FolderArrowRightFilled, FolderArrowRightRegular)

export function TimesHeaderActionsButtons(
    props: BaseMutableOverviewActionButtonProps<ExtendedTimeOverviewDto> & {
        addDisabled?: boolean
        moveDisabled?: boolean
        onMoveClick?: () => void
    },
) {
    const { t: translate } = useTranslation()

    return (
        <>
            <Tooltip withArrow relationship={'label'} content={translate('invoiceapproval.times.move.title')}>
                <Button icon={<MoveIcon />} disabled={props.moveDisabled} onClick={() => props.onMoveClick?.()} />
            </Tooltip>
            <Tooltip withArrow relationship={'label'} content={translate('action.create.time.title')}>
                <Button icon={<AddIcon />} appearance={'primary'} disabled={props.addDisabled} onClick={() => props.setOpenMutationDialog(true, null)} />
            </Tooltip>
        </>
    )
}

export type ViewInvoiceApprovalTimesProps = ViewInvoiceApprovalComponentProps & {
    setTimesCount: Dispatch<SetStateAction<number>>
    setTimes: Dispatch<SetStateAction<ExtendedTimeOverviewDto[]>>
    selectedTimes: ExtendedTimeOverviewDto[]
}

export function TimesHeaderFilterTags(props: BaseTimeOverviewProps & { userApprovals: UserInvoiceApprovalMutationDto[] }) {
    const { values, setValues } = useFormikContext<TimeFilterType>()
    const overviewStyles = useOverviewStyles()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNewWide), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {props.userApprovals?.map((item, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === item.userId}
                    onClick={() => setValues({ ...props.defaultFilterValues, users: [item.userId], tag: item.userId })}
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <PersonAvatar name={getUserById(item.userId)?.fullName} color={'colorful'} size={24} />
                </InteractionTag>
            ))}
        </div>
    )
}

export default function ViewInvoiceApprovalTimes(props: ViewInvoiceApprovalTimesProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const [moveDialogOpen, setMoveDialogOpen] = useState<boolean>(false)
    const [moveReloadTrigger, setMoveReloadTrigger] = useState<number>(0)

    const defaultFilterValues = useMemo<TimeFilterType>(
        () => ({
            from: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).start.toISODate(),
            to: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).end.toISODate(),
            users: [],
            isBilled: props.approvalStatus === InvoiceApprovalStatus.Done,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
            billable: [Billable.Billable, Billable.Offered],
            attributes: [],
            query: '',
            // The declaration has its own fixed period, so it must not inherit the interval tag of the time
            // overview - a tag would make filterFromJson recompute from/to and overwrite that period (HAM-4354).
            tag: '',
        }),
        [values, props.invoiceTemplate],
    )

    const fixedFilterValues = useMemo<TimeFilterType>(
        () => ({
            files: values.fileIds || [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values, props.invoiceTemplate],
    )

    // Default the new time entry to a date within the declaration's interval, so it shows up in this overview after saving.
    const mutationDate = useMemo<string>(() => {
        const today = DateTime.local().toISODate()
        if (defaultFilterValues.from && today < defaultFilterValues.from) {
            return defaultFilterValues.from
        }
        if (defaultFilterValues.to && today > defaultFilterValues.to) {
            return defaultFilterValues.to
        }
        return today
    }, [defaultFilterValues])

    const formWarning = useCallback(
        (mutation: TimeMutationDto): string | undefined => {
            const outsideInterval =
                !!mutation.date &&
                ((!!defaultFilterValues.from && mutation.date < defaultFilterValues.from) ||
                    (!!defaultFilterValues.to && mutation.date > defaultFilterValues.to))
            const outsideBillable = !!mutation.billable && ![Billable.Billable, Billable.Offered].includes(mutation.billable)
            const outsideFiles = !!mutation.fileId && !(values.fileIds || []).includes(mutation.fileId)

            return outsideInterval || outsideBillable || outsideFiles ? translate('invoiceapproval.times.add.warning') : undefined
        },
        [defaultFilterValues, values.fileIds, translate],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseTimeOverview
                    baseUrl={`/invoice-approval/${props.approval?.id}/time`}
                    hideFileField={values.fileIds?.length < 2}
                    hideInvoiceField
                    focusQueryOnRender={false}
                    renderLink={!!values.id}
                    noSticky
                    fileId={values.fileIds?.length === 1 ? values.fileIds[0] : undefined}
                    mutationDefaultValues={{ date: mutationDate }}
                    mutationDialogBodyProps={item => ({ item, formWarning })}
                    headerActionsButtons={TimesHeaderActionsButtons}
                    headerActionsButtonsProps={{
                        addDisabled: !props.isApprovalActive,
                        moveDisabled: !props.isApprovalActive || props.selectedTimes.length === 0,
                        onMoveClick: () => setMoveDialogOpen(true),
                    }}
                    reloadTrigger={moveReloadTrigger}
                    headerDefaultFilterTags={filterProps => (
                        <TimesHeaderFilterTags
                            {...(filterProps as BaseTimeOverviewProps)}
                            baseUrl={`/invoice-approval/${props.approval?.id}/time`}
                            userApprovals={values.userApprovals}
                        />
                    )}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(_column, result) => props.setTimesCount(result.total)}
                    onLoadItems={(_column, _columnPages, items) => props.setTimes([...items])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedTimes.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedTimes.includes(item)).length === 0}
                    useItemSelectedColor={false}
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('times', [...values.times, item.id])
                            if (values.id) {
                                axios.post<UserInvoiceApprovalDto[]>(`/finance/invoice-approval/${values.id}/time?id=${item.id}`).then(response => {
                                    if (!!response?.data) {
                                        setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                    }
                                })
                            }
                        } else {
                            setFieldValue('times', [...values.times.filter(time => time !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/time?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'times',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios
                                    .post<UserInvoiceApprovalDto[]>(
                                        `/finance/invoice-approval/${values.id}/time?id=${items.map(item => item.id).join(',')}`,
                                    )
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('times', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/time?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedTimes}
                />
            )}
            <MoveTimesDialog
                approvalId={values.id}
                selectedTimes={props.selectedTimes}
                open={moveDialogOpen}
                setOpen={setMoveDialogOpen}
                onMoved={movedIds => {
                    setFieldValue(
                        'times',
                        values.times.filter(time => !movedIds.includes(time)),
                    )
                    setMoveReloadTrigger(trigger => trigger + 1)
                    setMoveDialogOpen(false)
                }}
            />
        </div>
    )
}
